/**
 * Product Adder — Reston Farm Garden Market
 *
 * Two adder types:
 *   option_schedule — bakery pickup (required date/time and optional preference tiles)
 *   product         — firewood stacking fee (checkbox + optional delivery zone check)
 *
 * Horizons theme integration notes (derived from source):
 *   - Variant picker: <variant-picker> custom element, radio inputs in <fieldset> elements
 *     Radio value attr = human-readable option value. Change event on radio triggers variantChanged().
 *   - Cart events: 'cart:update' (ThemeEvents.cartUpdate) dispatched on product-form-component, bubbles.
 *   - Cart add: POSTs to Theme.routes.cart_add_url using FormData from <form> inside product-form-component.
 *   - ATC button: <add-to-cart-component> with [ref="addToCartButton"]. Theme enables/disables on variant:update.
 */
window.ProductAdder = (function () {
  'use strict';

  /* ── State ── */
  var root = null;
  var type = null;
  var section = null;
  var scheduleConfigurationError = false;

  var state = {
    /* option_schedule */
    selectedDate: null,    /* { iso, dayName, display } */
    selectedTime: null,    /* string */
    selectedChoices: {},   /* { groupLabel: choiceValue } */
    variantAvailable: true,
    /* product */
    checked: false,
    addressValidated: false,
    deliveryAddress: null,
    deliveryDistance: null,
    deliveryRate: null
  };

  /* ════════════════════════════════════════════════════════════
     INIT
     ════════════════════════════════════════════════════════════ */

  function init() {
    root = document.querySelector('.product-adder');
    if (!root) return;

    type = root.dataset.adderType;
    section = root.closest('.shopify-section');

    if (type === 'option_schedule') {
      initSchedule();
    } else if (type === 'product') {
      initProduct();
    }
  }

  /* ════════════════════════════════════════════════════════════
     TYPE: option_schedule
     ════════════════════════════════════════════════════════════ */

  function initSchedule() {
    if (section) section.classList.add('product-adder--schedule-active');
    var initialButton = section ? section.querySelector('[ref="addToCartButton"]') : null;
    state.variantAvailable = initialButton ? !initialButton.disabled : true;
    prepareVariantPicker();
    hideAcceleratedCheckout();
    lockAtc(true);

    /* Date tiles */
    root.querySelectorAll('.adder-date-tile').forEach(function (tile) {
      tile.addEventListener('click', function () { onDateSelect(tile); });
    });

    /* Time tiles */
    root.querySelectorAll('.adder-time-tile').forEach(function (tile) {
      tile.addEventListener('click', function () { onTimeSelect(tile); });
    });

    /* Option tiles */
    root.querySelectorAll('.adder-option-tile').forEach(function (tile) {
      tile.addEventListener('click', function () { onChoiceSelect(tile); });
    });

    /* Intercept form submission and require the pickup properties. */
    interceptScheduleSubmit();

    /* Re-lock ATC after theme re-enables it on variant:update */
    if (section) {
      section.addEventListener('variant:update', function (event) {
        state.variantAvailable = Boolean(event.detail && event.detail.resource && event.detail.resource.available);
        requestAnimationFrame(function () {
          prepareVariantPicker();
          hideAcceleratedCheckout();
          syncLineItemInputs();
          updateScheduleAtc();
        });
      });
    }

    updateScheduleAtc();
  }

  /* ── Native variant picker protection ── */

  function prepareVariantPicker() {
    scheduleConfigurationError = false;
    if (!section) return;
    var picker = section.querySelector('variant-picker');
    if (!picker) return;

    var visibleFieldsets = 0;
    var fieldsets = picker.querySelectorAll('fieldset');
    fieldsets.forEach(function (fieldset) {
      var legend = fieldset.querySelector('legend');
      var optionName = legend ? legend.textContent.trim().toLowerCase() : '';
      var inputs = fieldset.querySelectorAll('input[type="radio"]');
      var isLegacyScheduleOption =
        /(pickup|collect)/.test(optionName) && /(day|date|time)/.test(optionName);

      fieldset.classList.remove('product-adder-fieldset-hidden');

      if (isLegacyScheduleOption) {
        scheduleConfigurationError = true;
        fieldset.classList.add('product-adder-fieldset-hidden');
      } else if (inputs.length <= 1) {
        /* A one-value option is not a customer choice (for example, "Fresh Baked"). */
        fieldset.classList.add('product-adder-fieldset-hidden');
      } else {
        visibleFieldsets += 1;
      }
    });

    picker.classList.toggle('product-adder-picker-hidden', fieldsets.length > 0 && visibleFieldsets === 0);
  }

  /* ── Tile handlers ── */

  function onDateSelect(tileEl) {
    /* Deselect previous */
    root.querySelectorAll('.adder-date-tile--selected').forEach(function (el) {
      el.classList.remove('adder-date-tile--selected');
      el.setAttribute('aria-selected', 'false');
    });
    tileEl.classList.add('adder-date-tile--selected');
    tileEl.setAttribute('aria-selected', 'true');

    state.selectedDate = {
      iso: tileEl.dataset.date,
      dayName: tileEl.dataset.dayName,
      display: tileEl.dataset.dateDisplay
    };

    /* Reset downstream selections */
    state.selectedTime = null;
    state.selectedChoices = {};
    root.querySelectorAll('.adder-time-tile--selected').forEach(function (el) {
      el.classList.remove('adder-time-tile--selected');
      el.setAttribute('aria-selected', 'false');
    });
    root.querySelectorAll('.adder-option-tile--selected').forEach(function (el) {
      el.classList.remove('adder-option-tile--selected');
      el.setAttribute('aria-selected', 'false');
    });

    /* Unlock time tiles */
    var timeSection = root.querySelector('[data-section="times"]');
    if (timeSection) {
      timeSection.classList.remove('adder-section--locked');
      timeSection.querySelectorAll('.adder-time-tile').forEach(function (t) { t.disabled = false; });
    }

    /* Re-lock option groups */
    var optionSection = root.querySelector('[data-section="options"]');
    if (optionSection) {
      optionSection.classList.add('adder-section--locked');
      optionSection.querySelectorAll('.adder-option-tile').forEach(function (t) { t.disabled = true; });
    }

    syncLineItemInputs();
    updateScheduleAtc();
  }

  function onTimeSelect(tileEl) {
    root.querySelectorAll('.adder-time-tile--selected').forEach(function (el) {
      el.classList.remove('adder-time-tile--selected');
      el.setAttribute('aria-selected', 'false');
    });
    tileEl.classList.add('adder-time-tile--selected');
    tileEl.setAttribute('aria-selected', 'true');

    state.selectedTime = tileEl.dataset.time;

    /* Reset option selections */
    state.selectedChoices = {};
    root.querySelectorAll('.adder-option-tile--selected').forEach(function (el) {
      el.classList.remove('adder-option-tile--selected');
      el.setAttribute('aria-selected', 'false');
    });

    /* Unlock option groups + auto-select defaults */
    var optionSection = root.querySelector('[data-section="options"]');
    if (optionSection) {
      optionSection.classList.remove('adder-section--locked');
      optionSection.querySelectorAll('.adder-option-tile').forEach(function (t) { t.disabled = false; });
      optionSection.querySelectorAll('.adder-option-tile[data-default="true"]').forEach(function (t) {
        onChoiceSelect(t);
      });
    }

    syncLineItemInputs();
    updateScheduleAtc();
  }

  function onChoiceSelect(tileEl) {
    var groupLabel = tileEl.dataset.groupLabel;
    var group = tileEl.closest('.adder-option-group');
    if (group) {
      group.querySelectorAll('.adder-option-tile--selected').forEach(function (el) {
        el.classList.remove('adder-option-tile--selected');
        el.setAttribute('aria-selected', 'false');
      });
    }
    tileEl.classList.add('adder-option-tile--selected');
    tileEl.setAttribute('aria-selected', 'true');
    state.selectedChoices[groupLabel] = tileEl.dataset.choiceValue;

    syncLineItemInputs();
    updateScheduleAtc();
  }

  /* ── ATC state ── */

  function allScheduleSelectionsComplete() {
    if (scheduleConfigurationError) return false;
    if (!state.selectedDate || !state.selectedTime) return false;
    var complete = true;
    root.querySelectorAll('.adder-option-group[data-required="true"]').forEach(function (g) {
      if (!state.selectedChoices[g.dataset.groupLabel]) complete = false;
    });
    return complete;
  }

  function updateScheduleAtc() {
    var allComplete = allScheduleSelectionsComplete();
    var message = '';

    if (scheduleConfigurationError) {
      message = "Pickup scheduling isn't ready for this item. Please call the market and we'll help.";
    } else if (!state.selectedDate) {
      message = 'Select a day to continue';
    } else if (!state.selectedTime) {
      message = 'Select a time to continue';
    } else if (!allComplete) {
      root.querySelectorAll('.adder-option-group[data-required="true"]').forEach(function (g) {
        if (!state.selectedChoices[g.dataset.groupLabel] && !message) {
          message = 'Make a selection to continue';
        }
      });
    }

    var summaryEl = root.querySelector('[data-summary]');
    var messageEl = root.querySelector('[data-atc-message]');

    if (allComplete) {
      var parts = ['Pickup ' + state.selectedDate.display, state.selectedTime];
      Object.keys(state.selectedChoices).forEach(function (key) {
        parts.push(state.selectedChoices[key]);
      });
      if (summaryEl) { summaryEl.textContent = parts.join(' \u00B7 '); summaryEl.hidden = false; }
      if (messageEl) { messageEl.hidden = true; }
      lockAtc(false);
    } else {
      if (summaryEl) { summaryEl.hidden = true; }
      if (messageEl) { messageEl.textContent = message; messageEl.hidden = false; }
      lockAtc(true);
    }
  }

  /* ── Line item properties ── */

  function syncLineItemInputs() {
    var form = getProductForm();
    if (!form) return;

    /* Remove previous adder inputs */
    form.querySelectorAll('.adder-line-item-property').forEach(function (el) { el.remove(); });

    if (!state.selectedDate || !state.selectedTime) return;

    appendHiddenInput(form, 'properties[Pickup Date]', state.selectedDate.display);
    appendHiddenInput(form, 'properties[Pickup Time]', state.selectedTime);

    Object.keys(state.selectedChoices).forEach(function (groupLabel) {
      appendHiddenInput(form, 'properties[' + groupLabel + ']', state.selectedChoices[groupLabel]);
    });
  }

  function appendHiddenInput(form, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.className = 'adder-line-item-property';
    form.appendChild(input);
  }

  function interceptScheduleSubmit() {
    if (!section) return;

    /* Delegate from the stable section so variant re-renders cannot drop this guard. */
    section.addEventListener('submit', function (e) {
      if (!e.target.matches('product-form-component form')) return;
      if (!allScheduleSelectionsComplete()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        updateScheduleAtc();
        return;
      }

      syncLineItemInputs();
    }, true);
  }

  /* ════════════════════════════════════════════════════════════
     TYPE: product (firewood stacking)
     ════════════════════════════════════════════════════════════ */

  function initProduct() {
    var panel = root.querySelector('.adder-panel');
    if (!panel) return;

    /* Pre-checked state */
    if (panel.dataset.preChecked === 'true') {
      state.checked = true;
      handlePanelState(panel);
    }

    panel.addEventListener('click', function (e) {
      /* Ignore clicks inside address input or action buttons */
      if (e.target.closest('.adder-address-input, .adder-zone-retry, .adder-zone-uncheck')) return;
      state.checked = !state.checked;
      handlePanelState(panel);
    });

    panel.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Enter') {
        if (e.target.closest('.adder-address-input')) return;
        e.preventDefault();
        state.checked = !state.checked;
        handlePanelState(panel);
      }
    });

    interceptAtcForProduct(panel);
  }

  function handlePanelState(panel) {
    panel.classList.toggle('adder-panel--checked', state.checked);
    panel.setAttribute('aria-checked', String(state.checked));

    var addressArea = panel.querySelector('[data-address-area]');
    var zoneResult = panel.querySelector('[data-zone-result]');
    var hasApiKey = root.dataset.googleApiKey && root.dataset.googleApiKey.trim() !== '';

    if (state.checked && panel.dataset.showDelivery === 'true' && hasApiKey) {
      slideInAddressArea(addressArea);
      lockAtc(true);
    } else if (state.checked) {
      /* Checked — no delivery check needed (or no API key yet). ATC stays enabled. */
      lockAtc(false);
    } else {
      /* Unchecked — clean up */
      if (addressArea) { addressArea.style.display = 'none'; addressArea.innerHTML = ''; }
      if (zoneResult) { zoneResult.style.display = 'none'; zoneResult.innerHTML = ''; }
      panel.classList.remove('adder-panel--out-of-zone');
      state.addressValidated = false;
      state.deliveryAddress = null;
      state.deliveryDistance = null;
      state.deliveryRate = null;
      lockAtc(false);
    }
  }

  function slideInAddressArea(area) {
    if (!area) return;
    area.style.display = '';
    area.innerHTML =
      '<label class="adder-address-label">' +
        '<span class="adder-address-label__icon">\uD83D\uDCCD</span> Where should we deliver?' +
      '</label>' +
      '<input type="text" class="adder-address-input" data-address-input ' +
        'placeholder="Start typing your address\u2026" autocomplete="off">';

    var input = area.querySelector('[data-address-input]');
    if (input) {
      input.focus();

      /* If Google Places is already loaded, attach autocomplete */
      if (window.google && window.google.maps && window.google.maps.places) {
        attachAutocomplete(input);
      }
    }
  }

  /* ── Google Places ── */

  function initAutocomplete() {
    var input = document.querySelector('.adder-address-input');
    if (input) attachAutocomplete(input);
  }

  function attachAutocomplete(input) {
    if (!window.google || input._autocompleteAttached) return;
    input._autocompleteAttached = true;

    var autocomplete = new google.maps.places.Autocomplete(input, {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    });

    autocomplete.addListener('place_changed', function () {
      var place = autocomplete.getPlace();
      if (place && place.geometry) {
        onPlaceSelected(place);
      }
    });
  }

  function onPlaceSelected(place) {
    var lat = place.geometry.location.lat();
    var lng = place.geometry.location.lng();
    var address = place.formatted_address;
    checkDeliveryZone(lat, lng, address);
  }

  /* ── Delivery zone ── */

  function checkDeliveryZone(lat, lng, address) {
    var storeLat = parseFloat(root.dataset.storeLat);
    var storeLng = parseFloat(root.dataset.storeLng);
    var miles = haversineDistance(storeLat, storeLng, lat, lng);
    var rates = [];
    try { rates = JSON.parse(root.dataset.deliveryRates); } catch (e) { /* noop */ }

    var rate = lookupRate(miles, rates);
    if (rate !== null) {
      showInZone(address, miles, rate);
    } else {
      showOutOfZone(address, miles);
    }
  }

  function showInZone(address, miles, rate) {
    state.addressValidated = true;
    state.deliveryAddress = address;
    state.deliveryDistance = miles;
    state.deliveryRate = rate;

    var panel = root.querySelector('.adder-panel');
    if (panel) panel.classList.remove('adder-panel--out-of-zone');

    var zoneResult = root.querySelector('[data-zone-result]');
    if (zoneResult) {
      zoneResult.style.display = '';
      zoneResult.className = 'adder-zone-result adder-zone-valid';
      zoneResult.innerHTML =
        '<span class="adder-zone-valid__icon">\u2713</span>' +
        '<span class="adder-zone-valid__text">' +
          'Delivery to ' + escapeHtml(address) + ' (' + miles.toFixed(1) + ' mi) \u2014 +$' + rate +
        '</span>';
    }

    lockAtc(false);
  }

  function showOutOfZone(address, miles) {
    state.addressValidated = false;
    state.deliveryAddress = null;
    state.deliveryDistance = null;
    state.deliveryRate = null;

    var panel = root.querySelector('.adder-panel');
    if (panel) panel.classList.add('adder-panel--out-of-zone');

    var zoneResult = root.querySelector('[data-zone-result]');
    if (zoneResult) {
      zoneResult.style.display = '';
      zoneResult.className = 'adder-zone-result adder-zone-invalid';
      zoneResult.innerHTML =
        '<span class="adder-zone-invalid__icon">\u2715</span>' +
        '<span class="adder-zone-invalid__text">' +
          escapeHtml(address) + ' is ' + miles.toFixed(1) + ' miles away \u2014 outside our delivery area.' +
        '</span>' +
        '<div class="adder-zone-invalid__actions">' +
          '<button type="button" class="adder-zone-retry" data-zone-retry>Try another address</button>' +
          '<button type="button" class="adder-zone-uncheck" data-zone-uncheck>Remove stacking</button>' +
        '</div>';

      zoneResult.querySelector('[data-zone-retry]').addEventListener('click', function () {
        var input = root.querySelector('[data-address-input]');
        if (input) { input.value = ''; input.focus(); }
        zoneResult.style.display = 'none';
      });

      zoneResult.querySelector('[data-zone-uncheck]').addEventListener('click', function () {
        state.checked = false;
        handlePanelState(panel);
      });
    }

    lockAtc(true);
  }

  /* ── Cart intercept for dual product add ── */

  function interceptAtcForProduct(panel) {
    if (!section) return;
    var formComponent = section.querySelector('product-form-component');
    if (!formComponent) return;

    var isAddingAdder = false;

    /**
     * Listen for successful cart adds from the product form.
     * The Horizons theme dispatches 'cart:update' (CartAddEvent) on the product-form-component
     * after a successful POST to /cart/add.js. We piggyback on this to add the stacking product.
     */
    formComponent.addEventListener('cart:update', function (e) {
      if (isAddingAdder) return;
      if (!state.checked) return;
      if (!e.detail || !e.detail.data) return;
      if (e.detail.data.source !== 'product-form-component') return;
      if (e.detail.data.didError) return;

      var adderVariantId = panel.dataset.adderVariantId;
      if (!adderVariantId) return;

      isAddingAdder = true;

      var itemData = {
        id: parseInt(adderVariantId, 10),
        quantity: 1
      };

      /* Attach delivery address as line item property if validated */
      if (state.deliveryAddress) {
        itemData.properties = { 'Delivery Address': state.deliveryAddress };
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [itemData] })
      })
        .then(function (r) { return r.json(); })
        .then(function () { dispatchCartRefresh(); })
        .catch(function (err) { console.error('[ProductAdder] stacking add failed:', err); })
        .finally(function () { isAddingAdder = false; });
    });
  }

  /* ════════════════════════════════════════════════════════════
     SHARED UTILITIES
     ════════════════════════════════════════════════════════════ */

  /** Lock/unlock ATC through both native disabled state and a section class. */
  function lockAtc(locked) {
    if (!section) return;
    section.classList.toggle('product-adder--atc-locked', locked);

    section.querySelectorAll('[ref="addToCartButton"], .add-to-cart-button, [name="add"]').forEach(function (button) {
      if (locked) {
        button.dataset.productAdderLocked = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.productAdderLocked === 'true') {
        delete button.dataset.productAdderLocked;
        button.disabled = !state.variantAvailable;
        if (state.variantAvailable) {
          button.removeAttribute('aria-disabled');
        } else {
          button.setAttribute('aria-disabled', 'true');
        }
      }
    });
  }

  function hideAcceleratedCheckout() {
    if (!section) return;
    section.querySelectorAll('.accelerated-checkout-block').forEach(function (container) {
      container.hidden = true;
      container.setAttribute('aria-hidden', 'true');
    });
  }

  function getProductForm() {
    if (!section) return null;
    return section.querySelector('product-form-component form');
  }

  /** Haversine distance in miles. */
  function haversineDistance(lat1, lng1, lat2, lng2) {
    var R = 3959;
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function toRad(deg) { return deg * Math.PI / 180; }

  function lookupRate(miles, rates) {
    for (var i = 0; i < rates.length; i++) {
      if (miles >= rates[i].min && miles < rates[i].max) return rates[i].rate;
    }
    return null;
  }

  /**
   * Dispatch 'cart:update' so the Horizons cart drawer / bubble refreshes.
   * The theme's CartAddEvent uses event name 'cart:update' with { bubbles: true }.
   * Components listen on document and call fetchAndUpdateCartQuantity() when detail.resource is empty.
   */
  function dispatchCartRefresh() {
    var evt = new CustomEvent('cart:update', { bubbles: true, detail: {} });
    document.dispatchEvent(evt);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    init: init,
    initAutocomplete: initAutocomplete
  };
})();
